import axios, { AxiosResponse } from 'axios'
import { Contractor } from '../types/contractor'
import { Contract } from '../types/contracts'

enum RequestMethod {
  POST = 'POST',
  GET = 'GET',
  DELETE = 'DELETE',
  PUT = 'PUT'
}

const backendUrl = process.env.REACT_APP_BACKEND_URL ?? ''

export class RequestMaker {
  private jwtToken = window.localStorage.getItem('vrema-jwtToken')

  private updateJwtToken (): void {
    this.jwtToken = window.localStorage.getItem('vrema-jwtToken')
  }

  private async makeRequest (method: RequestMethod, endPoint: string, body: any, config: any): Promise<AxiosResponse> {
    const finalUrl = `${backendUrl}/${endPoint}`
    switch (method) {
      case RequestMethod.GET:
        return await axios.get(finalUrl, config)
      case RequestMethod.POST:
        return await axios.post(finalUrl, body, config)
      case RequestMethod.PUT:
        return await axios.put(finalUrl, body, config)
      case RequestMethod.DELETE:
        return await axios.delete(finalUrl, config)
    }
  }

  private async makeAuthenticatedRequest (method: RequestMethod, endPoint: string, body?: any, config?: Record<string, any>): Promise<AxiosResponse> {
    this.updateJwtToken()

    return await this.makeRequest(method, endPoint, body, {
      headers: {
        Authorization: `Bearer ${this.jwtToken ?? ''}`
      },
      ...config
    })
  }

  private async makeUnauthenticatedRequest (method: RequestMethod, endPoint: string, body?: any, config?: Record<string, any>): Promise<AxiosResponse> {
    return await this.makeRequest(method, endPoint, body, config)
  }

  public async getUserContext (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.GET, 'user/current')
  }

  public async getToken ({ username, password, googleToken }: { username?: string, password?: string, googleToken?: string }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'auth/login', {
      username,
      password,
      googleToken
    })
  }

  public async createContractor (contrator: Partial<Contractor>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'contractors', contrator)
  }

  public async updateContractor (contrator: Partial<Contractor>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'contractors', contrator)
  }

  public async deleteContractors (ids: number[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'contractors/delete-many', {
      ids
    })
  }

  public async createContract (contrat: Partial<Contract>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'contracts', contrat)
  }

  public async updateContract (contrat: Partial<Contract>): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'contracts', contrat)
  }

  public async deleteContracts (ids: number[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'contracts/delete-many', {
      ids
    })
  }

  public async generateReceipts (receipts: any[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'generate-receipts', {
      receipts
    }, {
      responseType: 'arraybuffer'
    })
  }

  public async generateContractReceipts (ids: number[]): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'generate-receipts/contract', {
      ids
    }, {
      responseType: 'arraybuffer'
    })
  }

  public async registerUser (user: {
    username: string
    password: string
    phoneNumber: string
    email: string
    captchaToken: string | null
  }): Promise<AxiosResponse> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'auth/register', user)
  }

  public async updateUserCompanyDetails (user: {
    name: string
    taxId: string
    street: string
    city: string
    zipCode: string
    accountNumber: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'user/company-details', user)
  }

  public async updateUserDetails (user: {
    username: string
    phoneNumber: string
    email: string
  }): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.PUT, 'user/details', user)
  }

  public async changePassword (
    previousPassword: string,
    password: string
  ): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'user/change-password', {
      previousPassword,
      password
    })
  }

  public async sendEmailCode (): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'activations/send-email', {}, { showLoader: false })
  }

  public async activateEmail (code: string): Promise<AxiosResponse> {
    return await this.makeAuthenticatedRequest(RequestMethod.POST, 'activations/activate-email', {
      code
    }, { showLoader: false })
  }

  public async emailOrUsernameExists (emailOrUsername: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'user/email-or-username-exists/' + emailOrUsername, {}, {
      showLoader: false
    })
  }

  public async phoneNumberExists (phoneNumber: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'user/phone-number-exists/' + phoneNumber, {}, {
      showLoader: false
    })
  }

  public async emailExists (email: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.GET, 'user/email-exists/' + email, {}, {
      showLoader: false
    })
  }

  public async sendForgotPasswordCode (emailOrUsername: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'forgot-password/send-email-code', {
      emailOrUsername
    })
  }

  public async validateForgotPasswordCode (emailOrUsername: string, code: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'forgot-password/validate-email-code', {
      emailOrUsername,
      code
    })
  }

  public async changeForgottenPassword (emailOrUsername: string, code: string, password: string): Promise<AxiosResponse<boolean>> {
    return await this.makeUnauthenticatedRequest(RequestMethod.POST, 'forgot-password/change', {
      emailOrUsername,
      code,
      password
    })
  }
}
